import { graphql, Link, useStaticQuery } from "gatsby";
import { GatsbyImage, StaticImage } from "gatsby-plugin-image";
import { BgImage } from "gbimage-bridge";
import gsap from "gsap";
import React, { useEffect, useState } from "react";
import { Button as BootstrapButton } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import NavDropdown from "react-bootstrap/NavDropdown";
import Offcanvas from "react-bootstrap/Offcanvas";
import Row from "react-bootstrap/Row";
import { FaEnvelope, FaPhoneAlt } from "react-icons/fa";
import { MdKeyboardArrowRight } from "react-icons/md";
import CustomDropdownIcon from "../images/Vector.svg";
import Button from "./Button";

const Navigation = () => {
	const data = useStaticQuery(graphql`
		query NavbarQuery {
			wpComponent(slug: { eq: "navbar" }) {
				navbarFields {
					servicesSection {
						description
						backgroundImage {
							altText
							localFile {
								childImageSharp {
									gatsbyImageData(
										formats: [WEBP, AUTO]
										quality: 100
										transformOptions: { cropFocus: CENTER, fit: COVER }
										layout: CONSTRAINED
										placeholder: BLURRED
									)
								}
							}
						}
						links {
							link {
								title
								url
							}
						}
						featuredArticles {
							... on WpPost {
								id
								title
								slug
								blogFields {
									description
									featuredImage {
										altText
										localFile {
											childImageSharp {
												gatsbyImageData(
													formats: [WEBP, AUTO]
													quality: 100
													transformOptions: { cropFocus: CENTER, fit: COVER }
													layout: CONSTRAINED
													placeholder: BLURRED
												)
											}
										}
									}
								}
							}
						}
					}
					aboutUsSection {
						description
						links {
							link {
								title
								url
							}
						}
						locations {
							address
							label
						}
						backgroundImage {
							altText
							localFile {
								childImageSharp {
									gatsbyImageData(
										formats: [WEBP, AUTO]
										quality: 100
										transformOptions: { cropFocus: CENTER, fit: COVER }
										layout: CONSTRAINED
										placeholder: BLURRED
									)
								}
							}
						}
					}
					industriesSection {
						description
						links {
							link {
								title
								url
							}
						}
						backgroundImage {
							altText
							localFile {
								childImageSharp {
									gatsbyImageData(
										formats: [WEBP, AUTO]
										quality: 100
										transformOptions: { cropFocus: CENTER, fit: COVER }
										layout: CONSTRAINED
										placeholder: BLURRED
									)
								}
							}
						}
						featuredCaseStudies {
							... on WpCaseStudy {
								id
								title
								slug
								caseStudyFields {
									description
									featuredImage {
										altText
										localFile {
											childImageSharp {
												gatsbyImageData(
													formats: [WEBP, AUTO]
													quality: 100
													transformOptions: { cropFocus: CENTER, fit: COVER }
													layout: CONSTRAINED
													placeholder: BLURRED
												)
											}
										}
									}
								}
							}
						}
					}
				}
			}
		}
	`);

	const { navbarFields } = data.wpComponent;

	const [Expanded, setExpanded] = useState(false);
	const [selectedDropdown, setSelectedDropdown] = useState("");

	useEffect(() => {
		const handleClose = (e) => {
			const navElement = document.querySelector("header"); // or a more specific selector for your nav
			const clickedInsideNav = navElement.contains(e.target);

			if (Expanded && !clickedInsideNav) {
				setTimeout(() => {
					setExpanded(false);
				}, 600);

				gsap.to(".navbar-bottom", {
					maxHeight: "0px",
					duration: 0.4,
					delay: 0.2,
				});
				gsap.to(`.nav-bg`, {
					opacity: 0,
					duration: 0.1,
					delay: 1,
				});
			}
		};

		const handleScroll = () => {
			if (Expanded) {
				setTimeout(() => {
					setExpanded(false);
				}, 600);

				gsap.to(".navbar-bottom", {
					maxHeight: "0px",
					duration: 0.4,
					delay: 0.2,
				});
				gsap.to(`.nav-bg`, {
					opacity: 0,
					duration: 0.1,
					delay: 1,
				});
			}
		};

		document.addEventListener("click", handleClose);
		window.addEventListener("scroll", handleScroll);

		return () => {
			document.removeEventListener("click", handleClose);
			window.removeEventListener("scroll", handleScroll);
		};
	}, [Expanded]);

	const handleClick = (title) => {
		if (selectedDropdown === title && Expanded === true) {
			setTimeout(() => {
				setExpanded(false);
			}, 600);
			gsap.to(".navbar-bottom", {
				maxHeight: "0px",
				duration: 0.4,
				delay: 0.2,
			});
			gsap.to(`.nav-bg`, {
				opacity: 0,
				duration: 0.1,
				delay: 1,
			});
		} else if (selectedDropdown === title && Expanded === false) {
			setExpanded(true);
			gsap.to(`.nav-bg`, {
				opacity: 1,
				duration: 0.1,
				delay: 0.1,
			});
			gsap.to(".navbar-bottom", {
				maxHeight: "800px",
				duration: 0.6,
				delay: 0.2,
			});
		} else if (selectedDropdown !== title && Expanded === false) {
			setSelectedDropdown(title);
			setExpanded(true);
			gsap.to(`.nav-bg`, {
				opacity: 1,
				duration: 0.1,
				delay: 0.1,
			});
			gsap.to(".navbar-bottom", {
				maxHeight: "800px",
				duration: 0.6,
				delay: 0.2,
			});
		} else if (selectedDropdown !== title && Expanded === true) {
			setSelectedDropdown(title);
		}
	};

	const servicesBgImage =
		navbarFields.servicesSection.backgroundImage?.localFile?.childImageSharp
			?.gatsbyImageData;
	const industriesBgImage =
		navbarFields.industriesSection.backgroundImage?.localFile?.childImageSharp
			?.gatsbyImageData;
	const aboutUsBgImage =
		navbarFields.aboutUsSection.backgroundImage?.localFile?.childImageSharp
			?.gatsbyImageData;

	return (
		<header>
			<div className="bg-white py-3">
				<Container>
					<Row className="align-items-center">
						<Col md={4}>
							<Link to="/">
								<StaticImage
									src="../images/icon.png"
									alt="Premier Support Services Logo"
									style={{ maxWidth: "135px" }}
								/>
							</Link>
						</Col>
						<Col xs={12} md={8} className="text-end d-none d-md-block">
							<div className="d-flex align-items-center gap-3 justify-content-end">
								<div className="d-flex align-items-center gap-2 ">
									<FaPhoneAlt className="text-primary" />
									<a
										className="outfit-regular"
										href="tel:08450555444"
										style={{ color: "#003366", textDecoration: "none" }}
									>
										08450 555 444
									</a>
								</div>
								<div className="d-flex align-items-center gap-2">
									<FaEnvelope className="text-primary" />
									<a
										className="outfit-regular"
										href="mailto:info@premiersupportservices.co.uk"
										style={{ color: "#003366", textDecoration: "none" }}
									>
										info@premiersupportservices.co.uk
									</a>
								</div>
							</div>
						</Col>
					</Row>
				</Container>
			</div>

			<Navbar bg="primary" expand="xl" sticky="top" className="py-3">
				<Container>
					<Navbar.Toggle aria-controls="offcanvasNavbar" />
					<BootstrapButton
						style={{ borderRadius: "0px" }}
						className=" px-3 d-md-none"
						variant="yellow"
						as={Link}
						to="/request-a-proposal"
					>
						Get in Touch
					</BootstrapButton>
					<Navbar.Offcanvas
						id="offcanvasNavbar"
						aria-labelledby="offcanvasNavbarLabel"
						placement="end"
					>
						<Offcanvas.Header
							className="bg-primary custom-close-button"
							closeButton
						>
							<BootstrapButton
								style={{ borderRadius: "0px" }}
								className=" px-3"
								variant="yellow"
								as={Link}
								to="/request-a-proposal"
							>
								Get in Touch
							</BootstrapButton>
						</Offcanvas.Header>
						<Offcanvas.Body className="bg-jason-small">
							<Nav className="flex-grow-1">
								<Nav.Link
									className=" me-2  outfit-regular  text-white d-none d-xxl-inline-block "
									onClick={() => handleClick("Services")}
								>
									<span className="d-flex align-items-center outfit-regular  text-white gap-2">
										Services{" "}
										<img width={13} src={CustomDropdownIcon} alt="Dropdown" />
									</span>
								</Nav.Link>
								<NavDropdown
									className="me-2 d-xxl-none"
									title={
										<span className="d-flex align-items-center outfit-regular   text-white gap-2">
											Services{" "}
											<img width={13} src={CustomDropdownIcon} alt="Dropdown" />
										</span>
									}
									id="services-dropdown"
								>
									{navbarFields.servicesSection.links.map((item, i) => (
										<NavDropdown.Item
											className="outfit-regular"
											as={Link}
											key={i}
											to={item.link.url}
										>
											{item.link.title}
										</NavDropdown.Item>
									))}
									<NavDropdown.Item
										className="outfit-regular"
										as={Link}
										to="/services"
									>
										View All
									</NavDropdown.Item>
								</NavDropdown>

								{/* Industries Desktop */}
								<Nav.Link
									className="me-2 outfit-regular  text-white d-none d-xxl-inline-block"
									onClick={() => handleClick("Industries")}
								>
									<span className="d-flex align-items-center outfit-regular  text-white gap-2">
										Industries{" "}
										<img width={13} src={CustomDropdownIcon} alt="Dropdown" />
									</span>
								</Nav.Link>
								{/* Industries Mobile */}
								<NavDropdown
									className="me-2 d-xxl-none"
									title={
										<span className="d-flex align-items-center outfit-regular  text-white gap-2">
											Industries <img src={CustomDropdownIcon} alt="Dropdown" />
										</span>
									}
									id="industries-dropdown"
								>
									{navbarFields.industriesSection.links.map((item, i) => (
										<NavDropdown.Item
											className="outfit-regular"
											as={Link}
											key={i}
											to={item.link.url}
										>
											{item.link.title}
										</NavDropdown.Item>
									))}
									<NavDropdown.Item
										className="outfit-regular"
										as={Link}
										to="/industries"
									>
										View All
									</NavDropdown.Item>
								</NavDropdown>

								<Nav.Link
									className="me-2 outfit-regular  text-white"
									as={Link}
									to="/case-studies"
								>
									Case Studies
								</Nav.Link>
								<Nav.Link
									className="me-2 outfit-regular  text-white"
									as={Link}
									to="/resources"
								>
									Resources
								</Nav.Link>

								{/* About Us Desktop */}
								<Nav.Link
									className="me-2 outfit-regular  text-white d-none d-xxl-inline-block"
									onClick={() => handleClick("AboutUs")}
								>
									<span className="d-flex align-items-center outfit-regular  text-white gap-2">
										About Us <img src={CustomDropdownIcon} alt="Dropdown" />
									</span>
								</Nav.Link>
								{/* About Us Mobile */}
								<NavDropdown
									className="me-2 d-xxl-none"
									title={
										<span className="d-flex align-items-center outfit-regular  text-white gap-2">
											About Us <img src={CustomDropdownIcon} alt="Dropdown" />
										</span>
									}
									id="about-dropdown"
								>
									{navbarFields.aboutUsSection.links.map((item, i) => (
										<NavDropdown.Item
											className="outfit-regular"
											as={Link}
											key={i}
											to={item.link.url}
										>
											{/* Check if the title includes the specific text and replace it with "ESG" */}
											{item.link.title ===
											"ESG (Environmental and Social Governance)"
												? "ESG"
												: item.link.title}
										</NavDropdown.Item>
									))}
								</NavDropdown>
							</Nav>
							<div className="d-block d-md-none mt-3">
								<div className="d-flex align-items-center mb-2 gap-2 ">
									<FaPhoneAlt className="text-yellow" />
									<a
										className="outfit-regular "
										href="tel:08450555444"
										style={{ color: "white", textDecoration: "none" }}
									>
										08450 555 444
									</a>
								</div>
								<div className="d-flex align-items-center gap-2">
									<FaEnvelope className="text-yellow" />
									<a
										className="outfit-regular"
										href="mailto:info@premiersupportservices.co.uk"
										style={{ color: "white", textDecoration: "none" }}
									>
										info@premiersupportservices.co.uk
									</a>
								</div>
							</div>
							<div className="d-xl-none mt-4">
								<Row className="g-3">
									<Col xs={6}>
										<BootstrapButton
											style={{ borderRadius: "0px" }}
											className="  w-100"
											variant="outline-white"
											as={Link}
											to="/intranet-login"
										>
											Staff Login
										</BootstrapButton>
									</Col>
									<Col xs={6}>
										<BootstrapButton
											style={{ borderRadius: "0px" }}
											className="  w-100 "
											variant="outline-white"
											as={Link}
											to="/client-hub-login"
										>
											Client Login
										</BootstrapButton>
									</Col>
								</Row>
							</div>
						</Offcanvas.Body>
					</Navbar.Offcanvas>
					<div className="d-none d-md-flex gap-3">
						<BootstrapButton
							style={{ borderRadius: "0px" }}
							className=" px-3"
							variant="outline-white"
							as={Link}
							to="/intranet-login"
						>
							Staff Login
						</BootstrapButton>
						<BootstrapButton
							style={{ borderRadius: "0px" }}
							className=" px-3"
							variant="outline-white"
							as={Link}
							to="/client-hub-login"
						>
							Client Login
						</BootstrapButton>
						<BootstrapButton
							style={{ borderRadius: "0px" }}
							className=" px-3"
							variant="yellow"
							as={Link}
							to="/request-a-proposal"
						>
							Get in Touch
						</BootstrapButton>
					</div>
				</Container>
			</Navbar>
			<div className={`bg-white w-100 position-relative`}>
				<div
					style={{ overflow: "hidden", zIndex: 4 }}
					className={`w-100 d-block position-absolute navbar-bottom dropdown-gradient`}
				>
					<div>
						<Container fluid className={`my-0`}>
							{selectedDropdown === "Services" && Expanded && (
								<Row className="dropdown-content">
									<Col md={3} className="p-0">
										<BgImage
											image={servicesBgImage}
											className="h-100 position-relative d-flex align-items-start"
										>
											<div
												style={{
													background:
														"linear-gradient(0deg, rgba(0, 48, 75, 0.80) 0%, rgba(0, 48, 75, 0.80) 100%)",
												}}
												className="position-absolute w-100 h-100 top-0 start-0"
											></div>
											<div
												style={{ zIndex: 6 }}
												className="p-4 py-lg-5 position-relative d-block"
											>
												<h3 className="text-white mb-3">Services</h3>
												<p className="text-white mb-4">
													{navbarFields.servicesSection.description}
												</p>
												<Button
													outlineTextColor="#fff"
													width="w-100 w-md-auto"
													colour="#FEDE34"
													text="Learn More"
													link="/services"
													isOutline={true}
													hoverColor="#000"
												/>
											</div>
										</BgImage>
									</Col>
									<Col md={6} className="py-4 px-5 pt-6">
										{navbarFields?.servicesSection?.links?.map((item, i) => (
											<Link
												key={i}
												to={item.link.url}
												className="d-block  text-decoration-none fs-5"
												style={{ color: "#5F5F5F" }}
												onMouseEnter={(e) => {
													const linkElement = e.currentTarget;
													const textElement = linkElement.querySelector("span");
													const arrowElement = linkElement.querySelector("svg");
													const borderElement =
														linkElement.querySelector(".border-bottom-div");

													gsap.to(textElement, {
														color: "#00304B",
														fontWeight: "bold",
														duration: 0.2,
													});
													gsap.to(arrowElement, {
														color: "#00304B",
														duration: 0.2,
													});
													gsap.to(borderElement, {
														borderColor: "#00304B",
														duration: 0.2,
													});
												}}
												onMouseLeave={(e) => {
													const linkElement = e.currentTarget;
													const textElement = linkElement.querySelector("span");
													const arrowElement = linkElement.querySelector("svg");
													const borderElement =
														linkElement.querySelector(".border-bottom-div");

													gsap.to(textElement, {
														color: "#5F5F5F",
														fontWeight: "normal",
														duration: 0.2,
													});
													gsap.to(arrowElement, {
														color: "#5F5F5F",
														duration: 0.2,
													});
													gsap.to(borderElement, {
														borderColor: "#CEE4FF",
														duration: 0.2,
													});
												}}
											>
												<div
													className="pb-2 mb-3 d-flex fs-6 align-items-center justify-content-between border-bottom-div"
													style={{ borderBottom: "1px solid #CEE4FF" }}
												>
													<span>{item.link.title}</span>
													<MdKeyboardArrowRight className="fs-6" />
												</div>
											</Link>
										))}
									</Col>
									<Col
										md={3}
										className="pt-5 pb-5 px-4"
										style={{ backgroundColor: "#001221" }}
									>
										<h4 className="mb-4 fs-4" style={{ color: "#FFFFFF" }}>
											Featured Articles
										</h4>
										{navbarFields?.servicesSection?.featuredArticles?.map(
											(article) => {
												const imageData =
													article.blogFields?.featuredImage?.localFile
														?.childImageSharp?.gatsbyImageData;
												return (
													<Row
														key={article.id}
														className="align-items-center mb-4"
													>
														<Col>
															<div
																style={{
																	overflow: "hidden",
																	borderRadius: "12px",
																}}
															>
																<GatsbyImage
																	image={imageData}
																	alt={
																		article.blogFields.featuredImage?.altText ||
																		article.title
																	}
																	style={{
																		width: "100%",
																		height: "130px",
																		objectFit: "cover",
																	}}
																/>
															</div>
														</Col>
														<Col>
															<div>
																<h5
																	className="fs-6"
																	style={{ color: "#FFFFFF" }}
																>
																	{article.title}
																</h5>
																<p className="mb-2 text-white">
																	{article.blogFields.description}
																</p>
																<a
																	href={`/resources/${article.slug}`}
																	className="text-decoration-underline"
																	style={{ color: "#FEDE34" }}
																>
																	Read more
																</a>
															</div>
														</Col>
													</Row>
												);
											}
										)}

										<a
											href="/resources"
											className="text-decoration-none  outfit-bold white-link-yellow d-inline-flex align-items-center"
										>
											View more{" "}
											<MdKeyboardArrowRight
												style={{ color: "#FEDE34" }}
												className="fs-4 ms-1"
											/>
										</a>
									</Col>
								</Row>
							)}

							{selectedDropdown === "Industries" && Expanded && (
								<Row className="dropdown-content">
									<Col md={3} className="p-0">
										<BgImage
											image={industriesBgImage}
											className="h-100 position-relative d-flex align-items-start"
										>
											<div
												style={{
													background:
														"linear-gradient(0deg, rgba(0, 48, 75, 0.80) 0%, rgba(0, 48, 75, 0.80) 100%)",
												}}
												className="position-absolute w-100 h-100 top-0 start-0"
											></div>
											<div
												style={{ zIndex: 6 }}
												className="p-4 py-lg-5 position-relative d-block"
											>
												<h3 className="text-white mb-3">Industries</h3>
												<p className="text-white mb-4">
													{navbarFields.industriesSection.description}
												</p>
												<Button
													outlineTextColor="#fff"
													width="w-100 w-md-auto"
													colour="#FEDE34"
													text="Learn More"
													link="/industries"
													isOutline={true}
												/>
											</div>
										</BgImage>
									</Col>
									<Col md={6} className="py-4 px-5 pt-6">
										{navbarFields?.industriesSection?.links?.map((item, i) => (
											<Link
												key={i}
												to={item.link.url}
												className="d-block  text-decoration-none fs-5"
												style={{ color: "#5F5F5F" }}
												onMouseEnter={(e) => {
													const linkElement = e.currentTarget;
													const textElement = linkElement.querySelector("span");
													const arrowElement = linkElement.querySelector("svg");
													const borderElement =
														linkElement.querySelector(".border-bottom-div");
													gsap.to(textElement, {
														color: "#00304B",
														fontWeight: "bold",
														duration: 0.2,
													});
													gsap.to(arrowElement, {
														color: "#00304B",
														duration: 0.2,
													});
													gsap.to(borderElement, {
														borderColor: "#00304B",
														duration: 0.2,
													});
												}}
												onMouseLeave={(e) => {
													const linkElement = e.currentTarget;
													const textElement = linkElement.querySelector("span");
													const arrowElement = linkElement.querySelector("svg");
													const borderElement =
														linkElement.querySelector(".border-bottom-div");
													gsap.to(textElement, {
														color: "#5F5F5F",
														fontWeight: "normal",
														duration: 0.2,
													});
													gsap.to(arrowElement, {
														color: "#5F5F5F",
														duration: 0.2,
													});
													gsap.to(borderElement, {
														borderColor: "#CEE4FF",
														duration: 0.2,
													});
												}}
											>
												<div
													className="pb-2 mb-3 d-flex fs-6 align-items-center justify-content-between border-bottom-div"
													style={{ borderBottom: "1px solid #CEE4FF" }}
												>
													<span>{item.link.title}</span>
													<MdKeyboardArrowRight className="fs-6" />
												</div>
											</Link>
										))}
									</Col>
									<Col
										md={3}
										className="pt-5 pb-5 px-4"
										style={{ backgroundColor: "#001221" }}
									>
										<h4 className="mb-4 fs-4" style={{ color: "#FFFFFF" }}>
											Featured Case Studies
										</h4>
										{navbarFields?.industriesSection?.featuredCaseStudies?.map(
											(cs) => {
												const imageData =
													cs.caseStudyFields?.featuredImage?.localFile
														?.childImageSharp?.gatsbyImageData;
												return (
													<Row key={cs.id} className="align-items-center pb-4">
														<Col>
															<div
																style={{
																	overflow: "hidden",
																	borderRadius: "12px",
																}}
															>
																<GatsbyImage
																	image={imageData}
																	alt={
																		cs.caseStudyFields.featuredImage?.altText ||
																		cs.title
																	}
																	style={{
																		width: "100%",
																		height: "130px",
																		objectFit: "cover",
																	}}
																/>
															</div>
														</Col>
														<Col>
															<div>
																<h5
																	className="fs-6"
																	style={{ color: "#FFFFFF" }}
																>
																	{cs.title}
																</h5>
																<p className="mb-2 text-white">
																	{cs.caseStudyFields.description}
																</p>
																<a
																	href={`/${cs.slug}`}
																	className="text-decoration-underline"
																	style={{ color: "#FEDE34" }}
																>
																	Read more
																</a>
															</div>
														</Col>
													</Row>
												);
											}
										)}
										<a
											href="/case-studies"
											className="text-decoration-none  outfit-bold white-link-yellow d-inline-flex align-items-center"
										>
											View more{" "}
											<MdKeyboardArrowRight
												style={{ color: "#FEDE34" }}
												className="fs-4 ms-1"
											/>
										</a>
									</Col>
								</Row>
							)}

							{selectedDropdown === "AboutUs" && Expanded && (
								<Row className="dropdown-content">
									<Col md={3} className="p-0">
										<BgImage
											image={aboutUsBgImage}
											className="h-100 position-relative d-flex align-items-start"
										>
											<div
												style={{
													background:
														"linear-gradient(0deg, rgba(0, 48, 75, 0.80) 0%, rgba(0, 48, 75, 0.80) 100%)",
												}}
												className="position-absolute w-100 h-100 top-0 start-0"
											></div>
											<div
												style={{ zIndex: 6 }}
												className="p-4 py-lg-5 position-relative d-block"
											>
												<h3 className="text-white mb-3">About Us</h3>
												<p className="text-white mb-4">
													{navbarFields.aboutUsSection.description}
												</p>
												<Button
													outlineTextColor="#fff"
													width="w-100 w-md-auto"
													colour="#FEDE34"
													text="Learn More"
													link="/about-us"
													isOutline={true}
												/>
											</div>
										</BgImage>
									</Col>
									<Col md={6} className="py-4 px-5 pt-6">
										{navbarFields?.aboutUsSection?.links?.map((item, i) => (
											<Link
												key={i}
												to={item.link.url}
												className="d-block  text-decoration-none fs-5"
												style={{ color: "#5F5F5F" }}
												onMouseEnter={(e) => {
													const linkElement = e.currentTarget;
													const textElement = linkElement.querySelector("span");
													const arrowElement = linkElement.querySelector("svg");
													const borderElement =
														linkElement.querySelector(".border-bottom-div");
													gsap.to(textElement, {
														color: "#00304B",
														fontWeight: "bold",
														duration: 0.2,
													});
													gsap.to(arrowElement, {
														color: "#00304B",
														duration: 0.2,
													});
													gsap.to(borderElement, {
														borderColor: "#00304B",
														duration: 0.2,
													});
												}}
												onMouseLeave={(e) => {
													const linkElement = e.currentTarget;
													const textElement = linkElement.querySelector("span");
													const arrowElement = linkElement.querySelector("svg");
													const borderElement =
														linkElement.querySelector(".border-bottom-div");
													gsap.to(textElement, {
														color: "#5F5F5F",
														fontWeight: "normal",
														duration: 0.2,
													});
													gsap.to(arrowElement, {
														color: "#5F5F5F",
														duration: 0.2,
													});
													gsap.to(borderElement, {
														borderColor: "#CEE4FF",
														duration: 0.2,
													});
												}}
											>
												<div
													className="pb-2 mb-3 d-flex fs-6 align-items-center justify-content-between border-bottom-div"
													style={{ borderBottom: "1px solid #CEE4FF" }}
												>
													<span>{item.link.title}</span>
													<MdKeyboardArrowRight className="fs-6" />
												</div>
											</Link>
										))}
									</Col>
									<Col
										md={3}
										className="pt-5 pb-5 px-4"
										style={{ backgroundColor: "#001221" }}
									>
										<h4 className="mb-4 fs-4" style={{ color: "#FFFFFF" }}>
											Offices
										</h4>
										{navbarFields?.aboutUsSection?.locations?.map((loc, i) => (
											<div key={i} className="mb-4 ">
												<h5 className="fs-6" style={{ color: "#FFFFFF" }}>
													{loc.label}
												</h5>
												<p className="mb-2 text-white">{loc.address}</p>
												<a
													href={`/contact-us#locations`}
													className="text-decoration-underline"
													style={{ color: "#FEDE34" }}
												>
													View in map
												</a>
											</div>
										))}
										<a
											href="/contact-us#locations"
											className="text-decoration-none  outfit-bold white-link-yellow d-inline-flex align-items-center"
										>
											View all locations{" "}
											<MdKeyboardArrowRight
												style={{ color: "#FEDE34" }}
												className="fs-4 ms-1"
											/>
										</a>
									</Col>
								</Row>
							)}
						</Container>
					</div>
				</div>
			</div>
		</header>
	);
};

export default Navigation;
